import {
  anchorPosition,
  formatMeasurement,
  type Evaluated,
  angleMarkNominal,
} from '@/domain/construction';
import { formulaLabel } from '@/domain/expression';
import { type Vec2 } from '@/domain/math';
import { LineStyle, PointStyle, type Style } from '@/domain/model';
import { type SolidCurve } from '@/domain/solid';
import { type Scene } from '@/engine';

import {
  angleMarkGeometry,
  rightAngleCorners,
  tickRadii,
  valuePosition,
} from '../painters/angleMark';
import { axisStroke, nameInk, numberInk, tickStroke } from '../painters/axesStyle';
import { clipLineToBounds, clipRayToBounds } from '../painters/clip';
import { dashedIfCovered } from '../painters/hiddenLines';
import { DEFAULT_NAME_DIRECTION } from '../painters/namePlacement';
import {
  angleValueSize,
  formatDegrees,
  labelSize,
  NAME_GAP,
  namePlacement,
} from '../painters/paintValue';
import { approximateMeasurer, layoutText } from '../text';

import {
  type Drawable,
  drawList,
  exportFrame,
  type ExportFrame,
  type FrameOptions,
  type ContentOptions,
} from './drawList';
import { DEFAULT_PAGE_SETUP, type PageSetup, planPrint, type PrintLayout } from './page';

/**
 * PDF export, written directly.
 *
 * ## Why no library
 *
 * A PDF of a line drawing is a short text file: a handful of objects, a content stream of
 * path operators, and a cross-reference table. The smallest capable library is a couple of
 * hundred kilobytes — larger than this entire application — and every one of them brings a
 * font subsetting engine, an image pipeline and an encryption layer that a geometry diagram
 * has no use for. This is the same judgement the project already made about the canvas
 * renderer, applied to the same kind of problem.
 *
 * It also keeps the export *vector*, which is the whole point: a teacher printing a
 * worksheet needs lines that stay sharp, not a bitmap of the screen.
 *
 * ## What it deliberately does not do
 *
 * - **No embedded fonts.** Labels use Helvetica, one of the fourteen faces every reader is
 *   required to have. Embedding a font means subsetting it, and subsetting is a larger
 *   problem than everything else here combined. The cost is that a label in a script the
 *   standard fonts do not cover will not render — recorded rather than hidden.
 * - **No transparency groups.** Per-object opacity is emitted through an `ExtGState`, which
 *   every reader since Acrobat 4 honours; blend modes are not used at all.
 * - **No images.**
 *
 * ## Coordinates
 *
 * A PDF page has its origin at the bottom-left with y increasing upward — the same handedness
 * as the world. So unlike SVG and canvas, **nothing is flipped here**: the transform is a
 * translation and a scale. `projectorFor` in `drawList` is therefore *not* used, and that is
 * the one place these three exporters legitimately differ.
 */

export interface PdfOptions extends FrameOptions, ContentOptions {
  /** Page background. Omitted leaves the page white, which is what paper is. */
  readonly background?: string;
  readonly title?: string;
  /**
   * Paper, orientation and tiling. Omitted keeps the page fitted to the drawing, which is
   * right for a figure going into a document and wrong for a printed sheet.
   */
  readonly page?: PageSetup;
  /**
   * Pictures, already decoded and re-encoded as baseline JPEG.
   *
   * Keyed by the `href` the document holds. Supplied by the caller because decoding needs a
   * canvas and a network of asynchronous events, neither of which belongs in a pure byte
   * writer — this file is a PDF *serialiser* and stays testable by being nothing else.
   *
   * Omitted, images are skipped. That is the honest behaviour for a caller with no way to
   * decode, and it is what the earlier milestones did for every caller.
   */
  readonly images?: ReadonlyMap<string, PdfImage>;
}

/**
 * A picture ready to be embedded.
 *
 * JPEG rather than raw pixels, and the reason is size. A PDF may carry a raw `/DeviceRGB`
 * image, but a 2 000-pixel photograph is twelve megabytes that way and would have to be
 * written into the file uncompressed — this writer has no deflate implementation and adding
 * one to save a format nobody asked for would be the wrong trade. Baseline JPEG passes
 * through untouched under `/DCTDecode`, which every PDF reader has implemented since 1993,
 * and `canvas.toDataURL` produces exactly that for free.
 */
export interface PdfImage {
  readonly width: number;
  readonly height: number;
  /** The JPEG bytes, one character per byte, as `encodeLatin1` expects. */
  readonly bytes: string;
}

/** PDF user-space units are points: 72 to the inch. */
export const POINTS_PER_INCH = 72;

export function toPdf(scene: Scene, options: PdfOptions = {}): Uint8Array {
  const drawables = drawList(scene, options.only);
  const setup = options.page ?? DEFAULT_PAGE_SETUP;

  /*
   * The content bounds come from the fitted frame, so the margin option still applies and a
   * label near the edge is not cropped. Print layout then decides how many sheets that area
   * needs and at what scale.
   */
  const contentFrame = exportFrame(drawables, options);
  const layout = planPrint(contentFrame.world, {
    ...setup,
    scale: options.scale ?? setup.scale,
  });

  /*
   * Names are assigned once, up front, so the content streams and the resource dictionary
   * cannot disagree. A stream referring to `/Im2` that no page declares produces a file which
   * opens blank in a strict reader and looks fine in a forgiving one — the worst kind of bug
   * to find later.
   */
  const names = new Map<string, string>();
  for (const href of options.images?.keys() ?? []) {
    names.set(href, `Im${String(names.size)}`);
  }

  const pages = layout.pages.map((page) => {
    const frame: ExportFrame = {
      world: page.world,
      width: layout.pageWidth,
      height: layout.pageHeight,
      scale: layout.scale,
    };
    return contentStream(drawables, frame, layout.margin, options.background, names);
  });

  const embedded = [...names].flatMap(([href, name]) => {
    const image = options.images?.get(href);
    return image === undefined ? [] : [{ name, image }];
  });

  return assemble(layout, pages, options.title ?? 'EduSketch drawing', embedded);
}

/* -------------------------------------------------------------- content stream */

function contentStream(
  drawables: readonly Drawable[],
  frame: ExportFrame,
  margin: number,
  background: string | undefined,
  imageNames: ReadonlyMap<string, string>
): string {
  const out: string[] = [];

  if (background !== undefined) {
    const [r, g, b] = rgb(background);
    out.push(`${n(r)} ${n(g)} ${n(b)} rg`, `0 0 ${n(frame.width)} ${n(frame.height)} re`, 'f');
  }

  /*
   * The margin is a clip and a translation rather than a smaller frame, and the clip is what
   * makes tiling work: a shape straddling two sheets is drawn on both and cut at the edge,
   * instead of appearing whole on each of them.
   */
  out.push(
    'q',
    `${n(margin)} ${n(margin)} ${n(frame.width - margin * 2)} ${n(frame.height - margin * 2)} re`,
    'W',
    'n',
    `1 0 0 1 ${n(margin)} ${n(margin)} cm`
  );

  for (const drawable of drawables) {
    out.push(...operatorsFor(drawable, frame, imageNames));

    if (drawable.label !== undefined) {
      const at = anchorPosition(drawable.value);
      if (at !== null) {
        out.push(
          'q',
          ...nameOperators(
            pageProjector(frame)(at),
            drawable.nameDirection,
            drawable.label,
            drawable.style,
            frame.scale
          ),
          'Q'
        );
      }
    }
  }

  out.push('Q');
  return out.join('\n');
}

/**
 * World → page. A translation and a scale; no flip, because a PDF page is already y-up.
 */
function pageProjector(frame: ExportFrame): (point: Vec2) => Vec2 {
  return (point) => ({
    x: (point.x - frame.world.minX) * frame.scale,
    y: (point.y - frame.world.minY) * frame.scale,
  });
}

function operatorsFor(
  drawable: Drawable,
  frame: ExportFrame,
  imageNames: ReadonlyMap<string, string>
): readonly string[] {
  const { value } = drawable;
  // A line the solid covers is dashed here rather than in the document, because whether it is
  // covered is a fact about the view and not a choice the author made.
  const style = dashedIfCovered(drawable.style, drawable.covered);
  const to = pageProjector(frame);
  const out: string[] = ['q', ...styleOperators(style, frame.scale)];

  switch (value.kind) {
    // A viewpoint is not on the page. Nothing to draw, and nothing to name.
    case 'space':
      break;

    case 'point':
      out.push(...pointOperators(to(value.point), style, frame.scale));
      break;

    case 'segment': {
      const a = to(value.segment.a);
      const b = to(value.segment.b);
      out.push(...pathOperators([a, b], false), 'S');
      // Heads the style asked for. See the canvas painter for why this is presentation.
      if (style.arrows === 'start' || style.arrows === 'both') {
        out.push(...arrowheadOperators(b, a, style, frame.scale));
      }
      if (style.arrows === 'end' || style.arrows === 'both') {
        out.push(...arrowheadOperators(a, b, style, frame.scale));
      }
      break;
    }

    case 'vector': {
      const from = to(value.from);
      const at = to(value.to);
      out.push(...pathOperators([from, at], false), 'S');
      out.push(...arrowhead(from, at, style, frame.scale));
      break;
    }

    case 'line':
    case 'ray': {
      const clipped =
        value.kind === 'line'
          ? clipLineToBounds(value.line, frame.world)
          : clipRayToBounds(value.ray, frame.world);
      if (clipped !== null) {
        out.push(...pathOperators([to(clipped.a), to(clipped.b)], false), 'S');
        // See the note in the canvas painter: the arrow is what tells a ray from a segment.
        if (value.kind === 'ray') {
          out.push(...arrowheadOperators(to(clipped.a), to(clipped.b), style, frame.scale));
        }
      }
      break;
    }

    case 'circle':
      out.push(
        ...ellipseOperators(
          to(value.circle.center),
          value.circle.radius * frame.scale,
          value.circle.radius * frame.scale,
          0
        ),
        'S'
      );
      break;

    case 'ellipse':
      out.push(
        ...ellipseOperators(
          to(value.ellipse.center),
          value.ellipse.rx * frame.scale,
          value.ellipse.ry * frame.scale,
          value.ellipse.rotation
        ),
        'S'
      );
      break;

    case 'arc':
      out.push(...arcOperators(value.arc, frame), 'S');
      break;

    /*
     * Filled once, outlined edge by edge when the edges disagree about being covered.
     *
     * A section through a solid has an edge lying in a face the reader can see and another
     * running through the middle of the block. One outline with one dash state prints the second
     * like the first, and the paper loses the cue the screen has — which matters more here than
     * anywhere, because paper is where a worksheet is read.
     */
    case 'polygon': {
      const screen = value.vertices.map(to);
      if (drawable.coveredEdges === undefined && drawable.liftedEdges === undefined) {
        out.push(...areaOperators(screen, style, frame.scale));
        out.push(...pathOperators(screen, true), 'S');
        break;
      }

      out.push(...areaOperators(screen, style, frame.scale));
      screen.forEach((point, index) => {
        // A side handed over to a segment of its own is not written again here.
        if (drawable.liftedEdges?.[index] === true) return;
        const next = screen[(index + 1) % screen.length] ?? point;
        const edge = dashedIfCovered(style, drawable.coveredEdges?.[index]);
        out.push('q', ...styleOperators(edge, frame.scale), ...pathOperators([point, next], false));
        out.push('S', 'Q');
      });
      break;
    }

    case 'polyline': {
      const screen = value.points.map(to);
      if (value.closed) out.push(...areaOperators(screen, style, frame.scale));
      out.push(...pathOperators(screen, value.closed), 'S');
      break;
    }

    /*
     * A separate path per piece, each stroked on its own.
     *
     * PDF would let all the pieces share one path with a `m` between them, and the output
     * would be identical — but a stroke per piece keeps this the same shape as the canvas and
     * SVG branches, which is what stops the three drifting apart.
     */
    /*
     * Three weights rather than one — see `axesStyle`, where the ordering is argued.
     *
     * Each group is its own `q`/`Q` pair because a PDF carries no per-path style: the graphics
     * state is what is in force when `S` runs, so changing weight means pushing a state and
     * popping it. The opacities these derive are registered automatically — `collectAlphas`
     * reads them back out of the emitted stream.
     */
    case 'axes': {
      const axis = axisStroke(style);
      const tick = tickStroke(style);

      // Ticks first, so an axis is never drawn under the marks that hang off it.
      out.push('q', ...styleOperators(tick, frame.scale));
      for (const segment of value.ticks) {
        out.push(...pathOperators([to(segment.a), to(segment.b)], false), 'S');
      }
      out.push('Q');

      out.push('q', ...styleOperators(axis, frame.scale));
      for (const segment of [value.xAxis, value.yAxis]) {
        out.push(...pathOperators([to(segment.a), to(segment.b)], false), 'S');
      }
      out.push(
        ...arrowheadOperators(to(value.origin), to(value.xAxis.b), axis, frame.scale),
        ...arrowheadOperators(to(value.origin), to(value.yAxis.b), axis, frame.scale),
        'Q'
      );

      const number = numberInk(style);
      for (const mark of value.marks) {
        out.push(
          'q',
          ...styleOperators(number, frame.scale),
          ...textOperators(to(mark.at), mark.text, number, frame.scale),
          'Q'
        );
      }

      const name = nameInk(style);
      for (const label of value.names) {
        out.push(
          'q',
          ...styleOperators(name, frame.scale),
          ...textOperators(to(label.at), label.text, name, frame.scale),
          'Q'
        );
      }
      break;
    }

    /*
     * The table, and the reason it survives being printed at all.
     *
     * Everything here is either a stroked segment or an ASCII string, because a PDF font in
     * this file reaches U+00FF and no further — so `∞`, `↗` and `‖` arrive as geometry from
     * the evaluator rather than as characters that would come out as `?`.
     */
    case 'graph-marks': {
      /*
       * The guides are dashed whatever the style says. A solid line from a point to the axis is
       * a *construction line* and reads as part of the figure; dashes are what a textbook uses
       * to mean "read this off". See the canvas painter, which does the same.
       *
       * Written as a dash operator either side rather than by restyling, because the page's
       * graphics state is set once per drawable — the dots and the numbers after it must not
       * inherit the pattern.
       */
      const pattern = DASH[LineStyle.Dashed];
      out.push(`[${pattern.map((d) => n(d * frame.scale)).join(' ')}] 0 d`);
      for (const rule of value.rules) {
        out.push(...pathOperators([to(rule.a), to(rule.b)], false), 'S');
      }
      out.push('[] 0 d');
      for (const point of value.points) {
        out.push(...pointOperators(to(point), style, frame.scale));
      }
      for (const mark of value.marks) {
        out.push('q', ...centredTextOperators(to(mark.at), mark.text, style, frame.scale), 'Q');
      }
      break;
    }

    case 'variation-table':
      for (const rule of value.rules) {
        out.push(...pathOperators([to(rule.a), to(rule.b)], false), 'S');
      }
      for (const mark of value.marks) {
        out.push('q', ...centredTextOperators(to(mark.at), mark.text, style, frame.scale), 'Q');
      }
      break;

    // The curve, and the formula beside it — see the SVG emitter, where the omission is recorded.
    case 'function-graph': {
      for (const segment of value.segments) {
        out.push(...pathOperators(segment.map(to), false), 'S');
      }
      const label = formulaLabel(value.expression);
      if (label !== null && value.labelAt !== null) {
        out.push('q', ...textOperators(to(value.labelAt), label, style, frame.scale), 'Q');
      }
      break;
    }

    case 'solid':
      out.push(
        ...(value.curves !== undefined && value.curves.length > 0
          ? solidCurveOperators(value.curves, value.straight, to, style, frame.scale)
          : solidOperators(value.visible, value.hidden, to, style, frame.scale))
      );
      break;

    case 'angle-mark':
      out.push(...angleMarkOperators(value, to, style, frame.scale));
      break;

    case 'text':
      out.push(...textOperators(to(value.position), value.content, style, frame.scale));
      break;

    case 'measurement':
      out.push(
        ...textOperators(to(value.position), formatMeasurement(value.value), style, frame.scale)
      );
      break;

    /*
     * `cm` places the image, because a PDF image XObject is always drawn into the unit square
     * and the transform is the only thing that says where and how big. The matrix carries the
     * width and height directly; there is no scaling operator to reach for.
     *
     * An image with no embedded counterpart draws nothing rather than a placeholder — a caller
     * that could not decode it has already reported that, and a grey box in a printed
     * worksheet is worse than a gap.
     */
    case 'image': {
      const name = imageNames.get(value.href);
      if (name === undefined) break;

      const corner = to(value.position);
      const width = value.width * frame.scale;
      const height = value.height * frame.scale;
      out.push(
        'q',
        `${n(width)} 0 0 ${n(height)} ${n(corner.x)} ${n(corner.y)} cm`,
        `/${name} Do`,
        'Q'
      );
      break;
    }
  }

  out.push('Q');
  return out;
}

function styleOperators(style: Style, scale: number): readonly string[] {
  const [r, g, b] = rgb(style.stroke);
  const [fr, fg, fb] = rgb(style.fill === 'transparent' ? style.stroke : style.fill);

  const out = [
    `${n(r)} ${n(g)} ${n(b)} RG`,
    `${n(fr)} ${n(fg)} ${n(fb)} rg`,
    `${n(style.strokeWidth * scale)} w`,
    '1 j',
    '1 J',
  ];

  const dash = DASH[style.lineStyle];
  out.push(dash.length === 0 ? '[] 0 d' : `[${dash.map((d) => n(d * scale)).join(' ')}] 0 d`);

  // Opacity lives in a graphics state dictionary; there is no inline operator for it.
  if (style.opacity !== 1) out.push(`/GS${alphaName(style.opacity)} gs`);

  return out;
}

const DASH: Readonly<Record<LineStyle, readonly number[]>> = {
  [LineStyle.Solid]: [],
  [LineStyle.Dashed]: [6, 4],
  [LineStyle.Dotted]: [1, 3],
};

function fillsShape(style: Style): boolean {
  return style.fillStyle !== 'none' && style.fill !== 'transparent';
}

/**
 * Everything that paints the *inside* of a closed shape, leaving the outline to the caller.
 *
 * Two fills, and until now PDF knew one. A **solid** fill is the `f` operator. A **hatch** has no
 * operator at all, so the shape is used as a clip and diagonals are stroked across it — which is
 * exactly what the canvas does, and is why the two now agree.
 *
 * The alternative was a PDF tiling pattern, which is a pattern dictionary, a resource entry and a
 * content stream of its own per colour. Clipping is a dozen operators and reuses the path already
 * computed. For a fill a figure uses on one or two shapes, that trade is not close.
 *
 * Returning the fill separately from the outline is what makes this possible: `B` fills and
 * strokes in one operator and consumes the path, so a hatched shape could never have been written
 * with it. The caller now emits the path twice, which is a few bytes and one clear rule.
 */
function areaOperators(points: readonly Vec2[], style: Style, scale: number): readonly string[] {
  if (!fillsShape(style) || points.length === 0) return [];
  if (style.fillStyle !== 'hatch') return [...pathOperators(points, true), 'f'];

  const xs = points.map((point) => point.x);
  const ys = points.map((point) => point.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const height = maxY - minY;
  const width = maxX - minX;
  if (!(width > 0) || !(height > 0)) return [];

  const step = HATCH_SPACING * scale;
  const lines: string[] = [];
  // Forty-five degrees, sweeping the intercept from one corner past the other so the whole box is
  // covered — the same sweep the canvas makes, so the density and angle match.
  for (let offset = -height; offset <= width; offset += step) {
    lines.push(`${n(minX + offset)} ${n(minY)} m`, `${n(minX + offset + height)} ${n(maxY)} l`);
  }

  return [
    'q',
    ...pathOperators(points, true),
    // Clip to the shape and discard the path; the outline is written again by the caller.
    'W',
    'n',
    ...strokeAs(style.fill),
    `${n(scale)} w`,
    '[] 0 d',
    ...lines,
    'S',
    'Q',
  ];
}

/** Between hatch lines, in drawing units. The canvas uses the same number in screen pixels. */
const HATCH_SPACING = 7;

function pathOperators(points: readonly Vec2[], closed: boolean): readonly string[] {
  const out: string[] = [];
  points.forEach((point, index) => {
    out.push(`${n(point.x)} ${n(point.y)} ${index === 0 ? 'm' : 'l'}`);
  });
  if (closed && points.length > 0) out.push('h');
  return out;
}

function pointOperators(centre: Vec2, style: Style, scale: number): readonly string[] {
  const r = (style.pointSize / 2) * scale;

  switch (style.pointStyle) {
    case PointStyle.Dot:
      // Filled with the stroke colour: a point's colour is its stroke everywhere else.
      return [...strokeColourAsFill(style), ...ellipseOperators(centre, r, r, 0), 'f'];

    case PointStyle.Circle:
      return [...ellipseOperators(centre, r, r, 0), 'S'];

    case PointStyle.Square:
      return [
        ...strokeColourAsFill(style),
        `${n(centre.x - r)} ${n(centre.y - r)} ${n(r * 2)} ${n(r * 2)} re`,
        'f',
      ];

    case PointStyle.Cross:
      return [
        `${n(centre.x - r)} ${n(centre.y - r)} m`,
        `${n(centre.x + r)} ${n(centre.y + r)} l`,
        `${n(centre.x + r)} ${n(centre.y - r)} m`,
        `${n(centre.x - r)} ${n(centre.y + r)} l`,
        'S',
      ];

    case PointStyle.Plus:
      return [
        `${n(centre.x - r)} ${n(centre.y)} m`,
        `${n(centre.x + r)} ${n(centre.y)} l`,
        `${n(centre.x)} ${n(centre.y - r)} m`,
        `${n(centre.x)} ${n(centre.y + r)} l`,
        'S',
      ];
  }
}

/**
 * One line of text centred on its point, for a table cell.
 *
 * PDF has no `text-anchor`, so the run is shifted by half its own width — the same hand
 * centring the angle mark's value uses. The width is estimated from the character count
 * because measuring properly would mean parsing the font's metrics, and a cell's contents are
 * two or three digits where the estimate is never out by enough to see.
 */
function centredTextOperators(
  at: Vec2,
  content: string,
  style: Style,
  scale: number
): readonly string[] {
  if (content === '') return [];
  const size = labelSize(style) * scale;
  const width = content.length * size * 0.5;

  return [
    ...strokeColourAsFill(style),
    'BT',
    `/F1 ${n(size)} Tf`,
    `1 0 0 1 ${n(at.x - width / 2)} ${n(at.y - size * 0.35)} Tm`,
    `(${escapeString(content)}) Tj`,
    'ET',
  ];
}

/** A colour as the *stroke* colour operator, for hatch lines drawn in the fill colour. */
function strokeAs(colour: string): readonly string[] {
  const [r, g, b] = rgb(colour);
  return [`${n(r)} ${n(g)} ${n(b)} RG`];
}

function strokeColourAsFill(style: Style): readonly string[] {
  const [r, g, b] = rgb(style.stroke);
  return [`${n(r)} ${n(g)} ${n(b)} rg`];
}

/**
 * An ellipse from four cubic Béziers.
 *
 * PDF has no arc operator — the only curve it knows is a cubic. `KAPPA` is the standard
 * control-point offset that makes a cubic match a quarter circle to within about one part
 * in ten thousand, which is far below the resolution of anything this will be printed on.
 */
const KAPPA = 0.5522847498307936;

function ellipseOperators(
  centre: Vec2,
  rx: number,
  ry: number,
  rotation: number,
  from?: number,
  sweep?: number
): readonly string[] {
  /*
   * A partial sweep is written as its own chain of cubics rather than as a clipped whole.
   *
   * Each is fitted over at most a quarter turn, where the standard `4/3·tan(θ/4)` handle
   * length is accurate to about one part in a hundred thousand of the radius — far below a
   * printer's dot at any size a page can hold.
   */
  if (from !== undefined && sweep !== undefined) {
    const cos = Math.cos(rotation);
    const sin = Math.sin(rotation);
    const at = (t: number): Vec2 => {
      const x = rx * Math.cos(t);
      const y = ry * Math.sin(t);
      return { x: centre.x + x * cos - y * sin, y: centre.y + x * sin + y * cos };
    };
    const slope = (t: number): Vec2 => {
      const x = -rx * Math.sin(t);
      const y = ry * Math.cos(t);
      return { x: x * cos - y * sin, y: x * sin + y * cos };
    };

    const pieces = Math.max(1, Math.ceil(Math.abs(sweep) / (Math.PI / 2)));
    const step = sweep / pieces;
    const handle = (4 / 3) * Math.tan(step / 4);

    const start = at(from);
    const partial = [`${n(start.x)} ${n(start.y)} m`];

    for (let piece = 0; piece < pieces; piece++) {
      const t0 = from + step * piece;
      const t1 = t0 + step;
      const p0 = at(t0);
      const p1 = at(t1);
      const d0 = slope(t0);
      const d1 = slope(t1);
      partial.push(
        `${n(p0.x + d0.x * handle)} ${n(p0.y + d0.y * handle)} ` +
          `${n(p1.x - d1.x * handle)} ${n(p1.y - d1.y * handle)} ` +
          `${n(p1.x)} ${n(p1.y)} c`
      );
    }

    return partial;
  }

  const cos = Math.cos(rotation);
  const sin = Math.sin(rotation);
  const at = (x: number, y: number): Vec2 => ({
    x: centre.x + x * cos - y * sin,
    y: centre.y + x * sin + y * cos,
  });

  const ox = rx * KAPPA;
  const oy = ry * KAPPA;

  const start = at(rx, 0);
  const out = [`${n(start.x)} ${n(start.y)} m`];

  const quadrant = (
    c1x: number,
    c1y: number,
    c2x: number,
    c2y: number,
    ex: number,
    ey: number
  ): void => {
    const c1 = at(c1x, c1y);
    const c2 = at(c2x, c2y);
    const end = at(ex, ey);
    out.push(`${n(c1.x)} ${n(c1.y)} ${n(c2.x)} ${n(c2.y)} ${n(end.x)} ${n(end.y)} c`);
  };

  quadrant(rx, oy, ox, ry, 0, ry);
  quadrant(-ox, ry, -rx, oy, -rx, 0);
  quadrant(-rx, -oy, -ox, -ry, 0, -ry);
  quadrant(ox, -ry, rx, -oy, rx, 0);
  out.push('h');

  return out;
}

/**
 * An arc, as a polyline.
 *
 * Flattened rather than fitted with Béziers. An arc is drawn once, at a resolution chosen
 * from its size on the page, and the error of a chord across a degree of arc is far below
 * a printer's dot. Bézier fitting would be more elegant and would buy nothing measurable.
 */
function arcOperators(
  arc: { center: Vec2; radius: number; startAngle: number; endAngle: number },
  frame: ExportFrame
): readonly string[] {
  const to = pageProjector(frame);

  let sweep = arc.endAngle - arc.startAngle;
  while (sweep <= 0) sweep += Math.PI * 2;

  const radiusOnPage = arc.radius * frame.scale;
  const steps = Math.max(8, Math.ceil((sweep / (Math.PI * 2)) * Math.max(24, radiusOnPage)));

  const points: Vec2[] = [];
  for (let i = 0; i <= steps; i++) {
    const angle = arc.startAngle + (sweep * i) / steps;
    points.push(
      to({
        x: arc.center.x + Math.cos(angle) * arc.radius,
        y: arc.center.y + Math.sin(angle) * arc.radius,
      })
    );
  }

  return pathOperators(points, false);
}

function arrowhead(from: Vec2, to: Vec2, style: Style, scale: number): readonly string[] {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const length = Math.hypot(dx, dy);
  if (length < 1e-9) return [];

  const size = Math.max(6, style.strokeWidth * 4) * scale;
  const ux = dx / length;
  const uy = dy / length;
  const spread = 0.42;

  const left = {
    x: to.x - size * (ux * Math.cos(spread) - uy * Math.sin(spread)),
    y: to.y - size * (uy * Math.cos(spread) + ux * Math.sin(spread)),
  };
  const right = {
    x: to.x - size * (ux * Math.cos(spread) + uy * Math.sin(spread)),
    y: to.y - size * (uy * Math.cos(spread) - ux * Math.sin(spread)),
  };

  return [...strokeColourAsFill(style), ...pathOperators([to, left, right], true), 'f'];
}

/** One drawn edge of a solid, on the page. */
interface Edge {
  readonly a: Vec2;
  readonly b: Vec2;
}

/**
 * A round solid written to the page as conics, with whatever straight parts it also has.
 *
 * Béziers rather than a flattened polyline, which is the one place in this writer where that
 * distinction earns its keep: a printed figure is enlarged by whoever reads it, and a chord
 * chain that looks smooth at a hundred per cent is a visibly kinked chain on a projector. The
 * arc writer next door flattens because an angle mark is a centimetre across and never grows;
 * a sphere is the drawing.
 *
 * A cylinder's sides stay straight lines — they are generator lines, and a conic there would
 * replace a right answer with a wrong one — and are written inside the same hidden-then-visible
 * passes so that nothing dashed lands on top of something solid.
 */
function solidCurveOperators(
  curves: readonly SolidCurve[],
  straight: { visible: readonly Edge[]; hidden: readonly Edge[] } | undefined,
  to: (point: Vec2) => Vec2,
  style: Style,
  scale: number
): readonly string[] {
  const out: string[] = [];

  // Hidden first, so a visible curve crossing one is drawn unbroken.
  for (const hidden of [true, false]) {
    const run = curves.filter((curve) => curve.hidden === hidden);
    const edges = (hidden ? straight?.hidden : straight?.visible) ?? [];
    if (run.length === 0 && edges.length === 0) continue;

    out.push('q');
    if (hidden) {
      out.push(
        `[${n(5 * scale)} ${n(4 * scale)}] 0 d`,
        `${n(Math.max(0.5, style.strokeWidth * 0.85) * scale)} w`,
        `/GS${alphaName(style.opacity * 0.45)} gs`
      );
    }

    for (const edge of edges) {
      out.push(...pathOperators([to(edge.a), to(edge.b)], false), 'S');
    }

    for (const curve of run) {
      out.push(
        ...ellipseOperators(
          to(curve.ellipse.center),
          curve.ellipse.rx * scale,
          curve.ellipse.ry * scale,
          curve.ellipse.rotation,
          curve.from,
          curve.sweep
        ),
        'S'
      );
    }
    out.push('Q');
  }

  return out;
}

function solidOperators(
  visible: readonly Edge[],
  hidden: readonly Edge[],
  to: (point: Vec2) => Vec2,
  style: Style,
  scale: number
): readonly string[] {
  const out: string[] = [];

  // Hidden first, so a visible edge crossing one is drawn unbroken.
  if (hidden.length > 0) {
    out.push(
      'q',
      `[${n(5 * scale)} ${n(4 * scale)}] 0 d`,
      `${n(Math.max(0.5, style.strokeWidth * 0.85) * scale)} w`,
      `/GS${alphaName(style.opacity * 0.45)} gs`
    );
    for (const edge of hidden) out.push(...pathOperators([to(edge.a), to(edge.b)], false));
    out.push('S', 'Q');
  }

  for (const edge of visible) out.push(...pathOperators([to(edge.a), to(edge.b)], false));
  if (visible.length > 0) out.push('S');

  return out;
}

/**
 * A label, laid out through the shared layout.
 *
 * Each run is positioned absolutely with its own `Td`, inside one `BT`/`ET` block. PDF's
 * text matrix is otherwise cumulative, and accumulating offsets is how a subscript drifts
 * out from under the letter it belongs to.
 *
 * Note the sign flip on the vertical offset: the layout puts a superscript at a *negative*
 * y because a canvas and an SVG run y downward, and a PDF page runs it upward.
 */
/**
 * An angle mark on a page.
 *
 * The arcs are flattened to short line segments rather than fitted with Béziers: a mark is
 * twenty-odd points across, so the error of a chord across a few degrees is far below a
 * printer's dot, and a Bézier fit would be more code for nothing anybody could see.
 */
function angleMarkOperators(
  value: Extract<Evaluated, { kind: 'angle-mark' }>,
  to: (point: Vec2) => Vec2,
  style: Style,
  scale: number
): readonly string[] {
  const geometry = angleMarkGeometry(value.vertex, value.from, value.to, value.armLength, to, {
    nominalRadius: angleMarkNominal(value.showValue) * scale,
    size: value.size,
    valueOffset: value.valueOffset,
  });
  if (geometry === null) return [];

  /*
   * The dash is carried, unlike on the other symbols: a mark sits in the corner between two
   * arms, and that corner is often inside the solid. `hiddenLines` has already decided, and a
   * hard-coded `[] 0 d` here would print a solid arc where the screen showed a dashed one.
   */
  const marks = DASH[style.lineStyle];
  const out: string[] = [
    `${n(Math.max(1, style.strokeWidth) * scale)} w`,
    marks.length === 0 ? '[] 0 d' : `[${marks.map((d) => n(d * scale)).join(' ')}] 0 d`,
    '0 j',
    '0 J',
  ];

  if (value.isRight) {
    const [first, far, second] = rightAngleCorners(geometry);
    out.push(...pathOperators([first, far, second], false), 'S');
  } else {
    for (const radius of tickRadii(geometry, value.ticks)) {
      out.push(...pathOperators(arcPoints(geometry, radius), false), 'S');
    }
  }

  if (value.showValue) {
    const at = valuePosition(geometry, value.ticks);
    const text = `${formatDegrees(value.degrees)}°`;
    const size = angleValueSize(style) * scale;
    // Centred by hand: PDF has no text-anchor, so the run is shifted by half its own width.
    const width = text.length * size * 0.5;
    out.push(
      ...strokeColourAsFill(style),
      'BT',
      `/F1 ${n(size)} Tf`,
      `1 0 0 1 ${n(at.x - width / 2)} ${n(at.y - size * 0.35)} Tm`,
      `(${escapeString(text)}) Tj`,
      'ET'
    );
  }

  return out;
}

function arcPoints(
  geometry: NonNullable<ReturnType<typeof angleMarkGeometry>>,
  radius: number
): readonly Vec2[] {
  let sweep = geometry.endAngle - geometry.startAngle;
  while (sweep <= -Math.PI) sweep += Math.PI * 2;
  while (sweep > Math.PI) sweep -= Math.PI * 2;

  const steps = Math.max(6, Math.ceil((Math.abs(sweep) / (Math.PI * 2)) * 48));
  return Array.from({ length: steps + 1 }, (_, index) => {
    const angle = geometry.startAngle + (sweep * index) / steps;
    return {
      x: geometry.vertex.x + Math.cos(angle) * radius,
      y: geometry.vertex.y + Math.sin(angle) * radius,
    };
  });
}

/**
 * Text that belongs where it was put: a note, a measurement, a number under an axis tick.
 *
 * Kept apart from {@link nameOperators} because those are pinned to their position and moving
 * them aside to dodge a line would detach them from what they describe. Only a name is free to
 * pick a side.
 */
function textOperators(at: Vec2, content: string, style: Style, scale: number): readonly string[] {
  const fontPx = labelSize(style) * scale;
  const layout = layoutText(content, { fontPx, measure: EXPORT_MEASURER });
  if (layout.lines.length === 0) return [];
  return runsFrom(at, layout, style);
}

/** An object's name, written in the direction the placement rule chose for it. */
function nameOperators(
  at: Vec2,
  direction: Vec2 | undefined,
  content: string,
  style: Style,
  scale: number
): readonly string[] {
  const fontPx = labelSize(style) * scale;
  const layout = layoutText(content, { fontPx, measure: EXPORT_MEASURER });
  if (layout.lines.length === 0) return [];

  // `namePlacement` answers in screen coordinates, where `y` grows downward; a PDF page grows
  // upward, so the vertical part is subtracted rather than added.
  const placed = namePlacement(
    direction ?? DEFAULT_NAME_DIRECTION,
    layout,
    fontPx,
    NAME_GAP * scale
  );
  return runsFrom({ x: at.x + placed.dx, y: at.y - placed.dy }, layout, style);
}

/** Emits one positioned run per glyph group. Each run carries its own size, so no font is set here. */
function runsFrom(
  origin: Vec2,
  layout: ReturnType<typeof layoutText>,
  style: Style
): readonly string[] {
  const out: string[] = [...strokeColourAsFill(style), 'BT'];

  layout.lines.forEach((line, index) => {
    const baseline = origin.y - index * layout.lineHeight;
    for (const run of line) {
      out.push(
        `/F1 ${n(run.fontPx)} Tf`,
        `1 0 0 1 ${n(origin.x + run.x)} ${n(baseline - run.y)} Tm`,
        `(${escapeString(run.text)}) Tj`
      );
    }
  });

  out.push('ET');
  return out;
}

/** Widths for an export, which has no canvas to measure with. */
const EXPORT_MEASURER = approximateMeasurer();

/* ------------------------------------------------------------------- assembly */

/**
 * Builds the file.
 *
 * Objects are written in order and their byte offsets recorded, because the cross-reference
 * table at the end has to give the exact offset of each one. Getting that wrong produces a
 * file that opens in a forgiving reader and fails in a strict one, which is the worst kind
 * of broken — so the offsets are measured from the bytes actually emitted rather than
 * predicted.
 */
function assemble(
  layout: PrintLayout,
  streams: readonly string[],
  title: string,
  images: readonly { readonly name: string; readonly image: PdfImage }[]
): Uint8Array {
  const alphas = collectAlphas(streams.join('\n'));

  const extGState =
    alphas.length === 0
      ? ''
      : `/ExtGState << ${alphas.map((a) => `/GS${a.name} << /CA ${a.value} /ca ${a.value} >>`).join(' ')} >>`;

  /*
   * Object numbering, laid out explicitly because the cross-reference table has to name every
   * one and a miscount produces a file that opens in a forgiving reader and is rejected by a
   * strict one:
   *
   *   1              catalogue
   *   2              page tree
   *   3              font
   *   4              metadata
   *   5 … 4+n        page objects
   *   5+n … 4+2n     content streams, one per page
   *   5+2n … 4+2n+m  image XObjects, one per distinct picture
   */
  const pageCount = streams.length;
  const firstPage = 5;
  const firstStream = firstPage + pageCount;
  const firstImage = firstStream + pageCount;

  const xObject =
    images.length === 0
      ? ''
      : `/XObject << ${images
          .map(({ name }, index) => `/${name} ${String(firstImage + index)} 0 R`)
          .join(' ')} >>`;

  const kids = streams.map((_, index) => `${String(firstPage + index)} 0 R`).join(' ');

  const objects: string[] = [
    '<< /Type /Catalog /Pages 2 0 R >>',
    `<< /Type /Pages /Kids [${kids}] /Count ${String(pageCount)} >>`,
    '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>',
    `<< /Title (${escapeString(title)}) /Producer (EduSketch) >>`,
  ];

  for (let index = 0; index < pageCount; index++) {
    objects.push(
      `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${n(layout.pageWidth)} ${n(layout.pageHeight)}] ` +
        `/Resources << /Font << /F1 3 0 R >> ${extGState} ${xObject} >> /Contents ${String(firstStream + index)} 0 R >>`
    );
  }

  for (const stream of streams) {
    objects.push(`<< /Length ${String(byteLength(stream))} >>\nstream\n${stream}\nendstream`);
  }

  /*
   * `/DCTDecode` hands the JPEG through untouched: the reader's own decoder does the work and
   * the file carries the picture at its compressed size. The filter is what makes that so —
   * without it the same bytes are read as raw samples and render as noise.
   *
   * `/Width` and `/Height` describe the *samples in the stream*, not the size the picture is
   * drawn at. The drawn size is carried entirely by the `cm` transform in the content stream,
   * because an image XObject is always painted into the unit square.
   */
  for (const { image } of images) {
    objects.push(
      `<< /Type /XObject /Subtype /Image /Width ${String(image.width)} /Height ${String(image.height)} ` +
        `/ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode ` +
        `/Length ${String(byteLength(image.bytes))} >>\nstream\n${image.bytes}\nendstream`
    );
  }

  let file = '%PDF-1.4\n';
  // A binary comment marks the file as non-text, so that transfers do not mangle it.
  file += '%âãÏÓ\n';

  const offsets: number[] = [];
  objects.forEach((body, index) => {
    offsets.push(byteLength(file));
    file += `${String(index + 1)} 0 obj\n${body}\nendobj\n`;
  });

  const xrefAt = byteLength(file);
  file += `xref\n0 ${String(objects.length + 1)}\n`;
  file += '0000000000 65535 f \n';
  for (const offset of offsets) {
    file += `${String(offset).padStart(10, '0')} 00000 n \n`;
  }

  file += `trailer\n<< /Size ${String(objects.length + 1)} /Root 1 0 R /Info 4 0 R >>\n`;
  file += `startxref\n${String(xrefAt)}\n%%EOF\n`;

  return encodeLatin1(file);
}

/** Every distinct alpha the content stream referenced, so each gets a state dictionary. */
function collectAlphas(content: string): readonly { name: string; value: string }[] {
  const found = new Map<string, string>();
  for (const match of content.matchAll(/\/GS([0-9]+) gs/g)) {
    const name = match[1];
    if (name === undefined) continue;
    found.set(name, n(Number(name) / 1000));
  }
  return [...found].map(([name, value]) => ({ name, value }));
}

/** Alpha as an integer per mille, so it can be part of a PDF name. */
function alphaName(opacity: number): string {
  return String(Math.max(0, Math.min(1000, Math.round(opacity * 1000))));
}

/* -------------------------------------------------------------------- helpers */

function n(value: number): string {
  if (!Number.isFinite(value)) return '0';
  const rounded = Math.round(value * 1000) / 1000;
  return String(rounded === 0 ? 0 : rounded);
}

/** `#rrggbb` to PDF's 0–1 components. Unknown formats fall back to black. */
function rgb(colour: string): readonly [number, number, number] {
  const hex = /^#([0-9a-f]{6})$/i.exec(colour.trim());
  if (hex === null) {
    const short = /^#([0-9a-f]{3})$/i.exec(colour.trim());
    if (short === null) return [0, 0, 0];
    const digits = short[1] ?? '000';
    return [
      parseInt(`${digits[0] ?? '0'}${digits[0] ?? '0'}`, 16) / 255,
      parseInt(`${digits[1] ?? '0'}${digits[1] ?? '0'}`, 16) / 255,
      parseInt(`${digits[2] ?? '0'}${digits[2] ?? '0'}`, 16) / 255,
    ];
  }
  const digits = hex[1] ?? '000000';
  return [
    parseInt(digits.slice(0, 2), 16) / 255,
    parseInt(digits.slice(2, 4), 16) / 255,
    parseInt(digits.slice(4, 6), 16) / 255,
  ];
}

/** Escapes the three characters that end a PDF literal string early. */
function escapeString(value: string): string {
  return value.replace(/[\\()]/g, (c) => `\\${c}`);
}

/**
 * Bytes the file will occupy once encoded.
 *
 * Exactly `text.length`, because `encodeLatin1` emits one byte per UTF-16 **code unit**.
 * The first version counted code points instead, which agrees for every character below
 * U+10000 and disagrees for anything above it: one emoji in a label would have shifted
 * every cross-reference offset after it by one byte and produced a file that opens in a
 * forgiving reader and fails in a strict one.
 */
function byteLength(text: string): number {
  return text.length;
}

/**
 * Encodes as Latin-1, one byte per character.
 *
 * A PDF string in `WinAnsiEncoding` is bytes, not UTF-8. Encoding the file as UTF-8 would
 * shift every byte offset after the first non-ASCII character and invalidate the
 * cross-reference table — a file that looks fine until somebody types an accent.
 * Characters outside the range become `?`, which is visible rather than silently corrupting.
 */
function encodeLatin1(text: string): Uint8Array {
  const bytes = new Uint8Array(text.length);
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i);
    bytes[i] = code > 0xff ? 0x3f : code;
  }
  return bytes;
}

/** Exposed for the tests, which read the file back rather than trusting it. */
export function decodeLatin1(bytes: Uint8Array): string {
  let text = '';
  for (const byte of bytes) text += String.fromCharCode(byte);
  return text;
}

export type { Evaluated };

/** A filled arrowhead for an axis, in page coordinates. */
function arrowheadOperators(from: Vec2, to: Vec2, style: Style, scale: number): readonly string[] {
  const away = { x: to.x - from.x, y: to.y - from.y };
  const length = Math.hypot(away.x, away.y);
  if (length < 1e-9) return [];

  const direction = { x: away.x / length, y: away.y / length };
  const across = { x: -direction.y, y: direction.x };

  const size = 9 * scale;
  const half = 3.5 * scale;
  const base = { x: to.x - direction.x * size, y: to.y - direction.y * size };

  return [
    ...strokeColourAsFill(style),
    `${n(to.x)} ${n(to.y)} m`,
    `${n(base.x + across.x * half)} ${n(base.y + across.y * half)} l`,
    `${n(base.x - across.x * half)} ${n(base.y - across.y * half)} l`,
    'h',
    'f',
  ];
}
